import { useState, useEffect, useRef } from 'react';
import {
  onAuthStateChanged, onIdTokenChanged, signOut,
  signInWithCustomToken, setPersistence, browserLocalPersistence,
} from 'firebase/auth';
import {
  deleteDoc, updateDoc, query, where, getDocs, getDoc, setDoc,
} from 'firebase/firestore';
import { auth, initialAuthToken, docRef } from '../../firebase/config';
import { syncLegacyProfileToCanonical } from '../../firebase/legacyProfileSync';
import {
  CANONICAL_APP_ID,
  LOGIN_LOOKUP_APP_IDS,
  docRefForApp,
  collectionRefForApp,
  isCanonicalAppId,
} from '../../firebase/dataPaths';
import { generateUserCode } from '../../utils/stringUtils';
import { normalizeEmail, emailsMatch } from '../../utils/emailUtils';
import { isPhoneLoginId } from '../../utils/phoneUtils';
import { resolveAuthIdentity } from '../../utils/authIdentity';
import { completePendingInstructorRegistration } from '../../services/registration';
import { hasPendingSignup } from '../../services/studentSignups';
import {
  extractIdentityValues,
  syncIdentities,
  isIdentityTakenError,
  describeIdentityError,
} from '../../services/identityUniqueness';
import {
  isExplicitLoginPending,
  markExplicitLoginIntent,
  isPortalAccessGranted,
  grantPortalAccess,
  revokePortalAccess,
  isAuthAwayExpired,
  markAuthAway,
  clearAuthAway,
} from '../../utils/historySession';
import {
  birlestirOgrenciBelgeleri,
  tasimaOgrenciAltVerileri,
} from '../../utils/studentLogic';
import { deleteAccountPermanently } from '../../services/accountDeletion';
import { resolvePostAuthPath } from '../../navigation/paths';
import {
  STUDENT_STATUS, INSTRUCTOR_STATUS, COLLECTIONS, STORAGE_KEYS, USER_ROLE,
  AUTH_AWAY_EXPIRED_MESSAGE, STUDENT_SIGNUP_PENDING_MESSAGE, STUDENT_EMAIL_ONLY_MESSAGE,
} from '../../utils/constants';

const bosSubVeri = () => ({
  swimmingRecords: [], programs: [], isLoading: false,
});

/** Öğrencinin Auth hesabında telefon da olabilir; giriş yöntemi yalnızca tokendan anlaşılır */
const isPhoneSignIn = async (user) => {
  try {
    return (await user.getIdTokenResult()).signInProvider === 'phone';
  } catch {
    return !user.email && Boolean(user.phoneNumber);
  }
};

const resolveRoleFromCol = (roleCol) =>
  roleCol === COLLECTIONS.INSTRUCTORS ? USER_ROLE.INSTRUCTOR : USER_ROLE.STUDENT;

const readUidProfile = async (roleCol, uid, normalizedEmail, appId = CANONICAL_APP_ID) => {
  const uidRef = docRefForApp(appId, roleCol, uid);
  const uidSnap = await getDoc(uidRef);
  if (!uidSnap.exists()) return null;

  const uidData = uidSnap.data();
  const resolvedRole = resolveRoleFromCol(roleCol);
  let uidEmail = normalizeEmail(uidData.emailOrPhone || normalizedEmail);
  const loginIsPhone = isPhoneLoginId(normalizedEmail);
  const storedIsEmail = uidEmail.includes('@');
  if (!emailsMatch(uidEmail, normalizedEmail) && !(loginIsPhone && storedIsEmail)) {
    uidEmail = normalizedEmail;
    if (appId === CANONICAL_APP_ID) {
      await updateDoc(uidRef, { emailOrPhone: uidEmail }).catch((e) =>
        console.warn('Profil e-posta güncellenemedi:', e)
      );
    }
  }

  return {
    profile: {
      ...uidData,
      id: uid,
      emailOrPhone: uidEmail,
      role: uidData.role || resolvedRole,
    },
    roleCol,
    sourceAppId: appId,
  };
};

const migrateLegacyProfile = async (roleCol, user, normalizedEmail, snap, sourceAppId) => {
  const resolvedRole = resolveRoleFromCol(roleCol);
  const canonicalUidRef = docRef(roleCol, user.uid);

  if (
    roleCol === COLLECTIONS.STUDENTS &&
    (snap.docs.length > 1 || !snap.docs.some((d) => d.id === user.uid))
  ) {
    const docsForMerge = snap.docs.map((d) => ({
      id: d.id,
      ref: d.ref,
      data: () => d.data(),
    }));
    const merged = await birlestirOgrenciBelgeleri(user.uid, docsForMerge);
    const mergedProfile = { ...merged, id: user.uid, role: USER_ROLE.STUDENT };
    const profile = await syncLegacyProfileToCanonical(roleCol, mergedProfile, sourceAppId);
    return { profile, roleCol, sourceAppId: CANONICAL_APP_ID };
  }

  const kaynakDoc = snap.docs.find((d) => d.id === user.uid) || snap.docs[0];
  const kaynakData = kaynakDoc.data();
  const normalizedProfileEmail = normalizeEmail(kaynakData.emailOrPhone || normalizedEmail);

  if (kaynakDoc.id !== user.uid && roleCol === COLLECTIONS.STUDENTS) {
    await setDoc(docRef(COLLECTIONS.STUDENTS, user.uid), {
      ...kaynakData,
      emailOrPhone: normalizedProfileEmail,
      role: kaynakData.role || resolvedRole,
    });
    await tasimaOgrenciAltVerileri(kaynakDoc.id, user.uid);
    await deleteDoc(kaynakDoc.ref).catch((e) =>
      console.warn('Eski öğrenci profili silinemedi:', e)
    );
  } else if (kaynakDoc.id !== user.uid && roleCol === COLLECTIONS.INSTRUCTORS) {
    await setDoc(docRef(COLLECTIONS.INSTRUCTORS, user.uid), {
      ...kaynakData,
      emailOrPhone: normalizedProfileEmail,
      role: kaynakData.role || resolvedRole,
    });
    await deleteDoc(kaynakDoc.ref).catch((e) =>
      console.warn('Eski eğitmen profili silinemedi:', e)
    );
  }

  const uidSnapAfter = await getDoc(canonicalUidRef);
  const profileData = {
    ...kaynakData,
    id: user.uid,
    emailOrPhone: normalizedProfileEmail,
    role: kaynakData.role || resolvedRole,
  };

  if (!uidSnapAfter.exists()) {
    await setDoc(canonicalUidRef, {
      ...kaynakData,
      emailOrPhone: normalizedProfileEmail,
      role: kaynakData.role || resolvedRole,
    });
  } else if (uidSnapAfter.data().emailOrPhone !== normalizedProfileEmail) {
    await updateDoc(canonicalUidRef, { emailOrPhone: normalizedProfileEmail }).catch((e) =>
      console.warn('Profil e-posta senkronu:', e)
    );
  }

  for (const d of snap.docs) {
    if (d.id !== user.uid && isCanonicalAppId(sourceAppId)) {
      await deleteDoc(docRefForApp(CANONICAL_APP_ID, roleCol, d.id)).catch((e) =>
        console.warn('Yinelenen profil silinemedi:', e)
      );
    }
  }

  return syncLegacyProfileToCanonical(roleCol, profileData, sourceAppId).then((profile) => ({
    profile,
    roleCol,
    sourceAppId: CANONICAL_APP_ID,
  }));
};

const emailLookupVariants = (user, normalizedEmail) =>
  [...new Set([
    normalizedEmail,
    normalizeEmail(user.email),
    user.email?.trim(),
    user.phoneNumber,
  ].filter(Boolean))];

const safeGetDocs = (q, label) =>
  getDocs(q).catch((err) => {
    console.warn(`Eski profil araması (${label}):`, err);
    return null;
  });

/** Tüm e-posta/telefon varyantlarını paralel sorgular; ilk eşleşen snapshot'ı döner */
const findLegacySnap = async (roleCol, user, normalizedEmail, appId) => {
  const col = collectionRefForApp(appId, roleCol);
  const lookups = emailLookupVariants(user, normalizedEmail).flatMap((identity) => [
    safeGetDocs(query(col, where('emailOrPhone', '==', identity)), `${roleCol}/${appId}/${identity}`),
    safeGetDocs(query(col, where('phoneNumber', '==', identity)), `${roleCol}/${appId}/${identity}`),
  ]);
  const snaps = await Promise.all(lookups);
  return snaps.find((snap) => snap && !snap.empty) || null;
};

const readLegacyProfile = async (roleCol, user, normalizedEmail, appId = CANONICAL_APP_ID) => {
  const snap = await findLegacySnap(roleCol, user, normalizedEmail, appId);
  if (!snap) return null;
  try {
    return await migrateLegacyProfile(roleCol, user, normalizedEmail, snap, appId);
  } catch (err) {
    console.warn(`Eski profil taşıma (${roleCol}, ${appId}):`, err);
    return null;
  }
};

const readUidProfileSafe = async (roleCol, uid, normalizedEmail, appId) => {
  try {
    return await readUidProfile(roleCol, uid, normalizedEmail, appId);
  } catch (err) {
    console.warn(`UID profil okuma (${roleCol}, ${appId}):`, err);
    return null;
  }
};

const LEGACY_APP_IDS = LOGIN_LOOKUP_APP_IDS.filter((id) => !isCanonicalAppId(id));

/** Önce kanonik yol; bulunamazsa eski app ID'ler tek turda paralel denenir */
const readUidProfileAnyApp = async (roleCol, uid, normalizedEmail) => {
  const canonical = await readUidProfileSafe(roleCol, uid, normalizedEmail, CANONICAL_APP_ID);
  if (canonical) return canonical;

  const results = await Promise.all(
    LEGACY_APP_IDS.map((appId) => readUidProfileSafe(roleCol, uid, normalizedEmail, appId))
  );
  const found = results.find(Boolean);
  if (!found) return null;
  try {
    const profile = await syncLegacyProfileToCanonical(roleCol, found.profile, found.sourceAppId);
    return { profile, roleCol: found.roleCol, sourceAppId: CANONICAL_APP_ID };
  } catch (err) {
    console.warn(`Eski profil senkronu (${roleCol}):`, err);
    return null;
  }
};

const readLegacyProfileAnyApp = async (roleCol, user, normalizedEmail) => {
  const canonical = await readLegacyProfile(roleCol, user, normalizedEmail, CANONICAL_APP_ID);
  if (canonical) return canonical;
  for (const appId of LEGACY_APP_IDS) {
    // eslint-disable-next-line no-await-in-loop
    const found = await readLegacyProfile(roleCol, user, normalizedEmail, appId);
    if (found) return found;
  }
  return null;
};

/** Mobil ağda profil çözümü sonsuz beklemesin */
const PROFILE_RESOLVE_TIMEOUT_MS = 25000;

const withProfileTimeout = (promise) => {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const err = new Error('PROFILE_TIMEOUT');
      err.code = 'profile-timeout';
      reject(err);
    }, PROFILE_RESOLVE_TIMEOUT_MS);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
};

/**
 * Kimlik doğrulama, oturum ve hesap silme işlemleri.
 * Girişte yalnızca UID okuması kullanılır; e-posta sorgusu yalnızca yedek olarak dener.
 */
export const useKimlik = ({ showToast, navigation, setMySubData, setActiveModal }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const isInitialAuthRef = useRef(true);
  const authRunIdRef = useRef(0);

  useEffect(() => {
    const initAuth = async () => {
      try {
        await setPersistence(auth, browserLocalPersistence);
        if (initialAuthToken && !sessionStorage.getItem(STORAGE_KEYS.AUTO_LOGIN_DONE)) {
          sessionStorage.setItem(STORAGE_KEYS.AUTO_LOGIN_DONE, 'true');
          await signInWithCustomToken(auth, initialAuthToken);
        }
      } catch (err) {
        console.warn('Custom token girişi başarısız:', err);
      }
    };
    initAuth();
  }, []);

  // Aynı hesaba yeniden giriş (ör. e-posta değişikliği) onAuthStateChanged tetiklemez;
  // yeni kullanıcı nesnesi alınmazsa dinleyiciler eski e-postayla kalır.
  useEffect(() => onIdTokenChanged(auth, (user) => {
    setFirebaseUser((prev) => (prev && user && prev !== user && prev.uid === user.uid ? user : prev));
  }), []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      const runId = ++authRunIdRef.current;
      const showFullScreenLoading = isInitialAuthRef.current;

      if (user?.email || user?.phoneNumber) {
        setIsLoading(true);
      } else if (showFullScreenLoading) {
        setIsLoading(true);
      }

      try {
        setFirebaseUser(user);
          if (user && (user.email || user.phoneNumber)) {
          const resolveProfile = async () => {
            const [{ loginId }, phoneSignIn] = await Promise.all([
              resolveAuthIdentity(user),
              isPhoneSignIn(user),
            ]);

            const intendedRole = window.localStorage.getItem(STORAGE_KEYS.INTENDED_ROLE);
            const lastRole = window.localStorage.getItem(STORAGE_KEYS.LAST_ROLE);
            // SMS yalnızca eğitmenler içindir; eğitmen profili varsa öğrenci profiline düşmesin
            const preferredRole = phoneSignIn
              ? USER_ROLE.INSTRUCTOR
              : intendedRole === USER_ROLE.INSTRUCTOR || intendedRole === USER_ROLE.STUDENT
                ? intendedRole
                : lastRole === USER_ROLE.INSTRUCTOR || lastRole === USER_ROLE.STUDENT
                  ? lastRole
                  : USER_ROLE.STUDENT;

            const studentLookup = readUidProfileAnyApp(COLLECTIONS.STUDENTS, user.uid, loginId);
            const instructorLookup = readUidProfileAnyApp(COLLECTIONS.INSTRUCTORS, user.uid, loginId);
            const [preferredLookup, otherLookup] = preferredRole === USER_ROLE.INSTRUCTOR
              ? [instructorLookup, studentLookup]
              : [studentLookup, instructorLookup];
            // Tercih edilen rol bulunduysa diğer rolün eski app ID aramaları beklenmez
            let found = (await preferredLookup) || (await otherLookup);

            if (!found) {
              const legacyOrder =
                preferredRole === USER_ROLE.INSTRUCTOR
                  ? [COLLECTIONS.INSTRUCTORS, COLLECTIONS.STUDENTS]
                  : [COLLECTIONS.STUDENTS, COLLECTIONS.INSTRUCTORS];

              for (const roleCol of legacyOrder) {
                // eslint-disable-next-line no-await-in-loop
                const legacy = await readLegacyProfileAnyApp(roleCol, user, loginId);
                if (legacy) {
                  found = legacy;
                  break;
                }
              }
            }
            return { normalizedEmail: loginId, found, phoneSignIn };
          };

          const { normalizedEmail, found: resolved, phoneSignIn } =
            await withProfileTimeout(resolveProfile());

          if (resolved?.profile) {
            if (runId !== authRunIdRef.current) return;

            const { profile: foundProfile, roleCol: activeRoleCol } = resolved;
            const profileRole =
              foundProfile.role || resolveRoleFromCol(activeRoleCol);
            const normalizedProfile = { ...foundProfile, role: profileRole };

            if (phoneSignIn && profileRole === USER_ROLE.STUDENT) {
              setLoggedInUser(null);
              showToast(STUDENT_EMAIL_ONLY_MESSAGE, 'error');
              await signOut(auth);
              navigation.goToLogin();
              return;
            }

            const mayEnterPortal =
              isExplicitLoginPending() ||
              isPortalAccessGranted(user.uid) ||
              Boolean(user.phoneNumber || user.email);

            if (!mayEnterPortal) {
              setLoggedInUser(null);
              setIsLoading(false);
              return;
            }

            window.localStorage.removeItem(STORAGE_KEYS.INTENDED_ROLE);
            window.localStorage.setItem(STORAGE_KEYS.LAST_ROLE, profileRole);

            const askida =
              normalizedProfile.status === INSTRUCTOR_STATUS.SUSPENDED ||
              normalizedProfile.status === STUDENT_STATUS.SUSPENDED;
            if (askida) {
              showToast('Hesabınız askıya alınmıştır.', 'error');
              await signOut(auth);
              return;
            }
            if (!normalizedProfile.userCode) {
              normalizedProfile.userCode = generateUserCode();
              await updateDoc(docRef(activeRoleCol, normalizedProfile.id), {
                userCode: normalizedProfile.userCode,
              }).catch((e) => console.warn('UserCode backfill başarısız:', e));
            }
            const wasExplicitLogin = isExplicitLoginPending();
            if (!wasExplicitLogin && isAuthAwayExpired()) {
              clearAuthAway();
              window.sessionStorage.removeItem('finly_resume_auth');
              revokePortalAccess();
              setLoggedInUser(null);
              showToast(AUTH_AWAY_EXPIRED_MESSAGE, 'error');
              await signOut(auth);
              navigation.goToLogin();
              setIsLoading(false);
              return;
            }
            clearAuthAway();
            grantPortalAccess(user.uid);
            // Başarılı çözümden sonra giriş ekranı oturumu yeniden sürdürebilsin (tek seferlik yenileme bayrağı)
            window.sessionStorage.removeItem('finly_resume_auth');
            setLoggedInUser(normalizedProfile);

            // Mevcut hesapların e-posta/telefonunu tekillik indeksine yaz (sessiz).
            syncIdentities(normalizedProfile.id, {
              claim: extractIdentityValues(normalizedProfile),
            }).catch((e) => console.warn('Kimlik rezervasyonu:', e));

            const targetPath = resolvePostAuthPath(window.location.pathname, profileRole);
            if (targetPath !== window.location.pathname) {
              navigation.navigate(targetPath, { replace: true });
            }

            if (wasExplicitLogin) {
              showToast('Giriş başarılı!');
            }
          } else {
            if (runId !== authRunIdRef.current) return;
            let createdInstructor = false;
            try {
              createdInstructor = Boolean(await completePendingInstructorRegistration(user));
            } catch (regErr) {
              console.error('Eğitmen kaydı tamamlanamadı:', regErr);
              showToast(
                isIdentityTakenError(regErr)
                  ? describeIdentityError(regErr)
                  : regErr.code === 'permission-denied'
                    ? 'Hesap oluşturulamadı. Sayfayı yenileyip tekrar deneyin.'
                    : 'Eğitmen kaydı tamamlanamadı. Tekrar deneyin.',
                'error'
              );
              await signOut(auth);
              navigation.goToLogin();
              setIsLoading(false);
              return;
            }
            if (createdInstructor) {
              markExplicitLoginIntent();
              window.localStorage.setItem(STORAGE_KEYS.INTENDED_ROLE, USER_ROLE.INSTRUCTOR);
              window.localStorage.setItem(STORAGE_KEYS.LAST_ROLE, USER_ROLE.INSTRUCTOR);
              const [instructorFromUid] = await Promise.all([
                readUidProfileAnyApp(COLLECTIONS.INSTRUCTORS, user.uid, normalizedEmail),
              ]);
              if (instructorFromUid?.profile) {
                const foundProfile = instructorFromUid.profile;
                const normalizedProfile = {
                  ...foundProfile,
                  role: foundProfile.role || USER_ROLE.INSTRUCTOR,
                };
                clearAuthAway();
                grantPortalAccess(user.uid);
                setLoggedInUser(normalizedProfile);
                const targetPath = resolvePostAuthPath(
                  window.location.pathname,
                  USER_ROLE.INSTRUCTOR
                );
                if (targetPath !== window.location.pathname) {
                  navigation.navigate(targetPath, { replace: true });
                }
                showToast('Eğitmen kaydınız tamamlandı. Hoş geldiniz!');
                setIsLoading(false);
                return;
              }
            }
            setLoggedInUser(null);
            const signupPending = Boolean(user.email) && await hasPendingSignup(user.email);
            showToast(
              signupPending
                ? STUDENT_SIGNUP_PENDING_MESSAGE
                : user.phoneNumber && !user.email
                  ? 'Bu telefon hesaba bağlı değil. E-posta kodu ile girin; SMS için hesabınıza numara ekleyin.'
                  : 'Hesap profiliniz bulunamadı. Öğrenciyseniz giriş ekranından kayıt olun; eğitmen hesabı için yöneticinize başvurun.',
              signupPending ? 'info' : 'error'
            );
            await signOut(auth);
            navigation.goToLogin();
          }
        } else {
          if (runId !== authRunIdRef.current) return;
          setLoggedInUser(null);
          setMySubData(bosSubVeri());
          navigation.goToLandingUnlessAuthScreen();
        }
      } catch (err) {
        if (runId !== authRunIdRef.current) return;
        console.error('Auth durumu işleme hatası:', err);
        setLoggedInUser(null);
        setMySubData(bosSubVeri());
        if (err.code === 'profile-timeout') {
          await signOut(auth).catch(() => {});
          navigation.goToLogin();
        }
        showToast(
          err.code === 'profile-timeout'
            ? 'Bağlantı yavaş olduğu için giriş tamamlanamadı. Tekrar giriş yapın.'
            : err.code === 'permission-denied'
              ? 'Giriş tamamlanamadı. Sayfayı yenileyip tekrar deneyin.'
              : 'Bağlantı sorunu oluştu. Lütfen sayfayı yenileyin.',
          'error'
        );
      } finally {
        if (runId === authRunIdRef.current) {
          setIsLoading(false);
          if (showFullScreenLoading) {
            isInitialAuthRef.current = false;
          }
        }
      }
    });
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Kullanımdayken oturum açık kalır.
   * Sekme/tarayıcı terk edilince uzak kalma sayacı başlar;
   * 6 saat içinde dönülmezse tekrar giriş istenir (uygulama kapatılsa da sayaç cihazda tutulur).
   */
  useEffect(() => {
    if (!loggedInUser) return undefined;

    let ending = false;
    const endIfAwayTooLong = async () => {
      if (ending || !isAuthAwayExpired()) {
        if (!isAuthAwayExpired()) clearAuthAway();
        return;
      }
      ending = true;
      clearAuthAway();
      window.sessionStorage.removeItem('finly_resume_auth');
      revokePortalAccess();
      setLoggedInUser(null);
      try {
        await signOut(auth);
      } catch (err) {
        console.warn('Uzak kalma oturum signOut:', err);
      }
      showToast(AUTH_AWAY_EXPIRED_MESSAGE, 'error');
      navigation.goToLogin();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        markAuthAway();
        return;
      }
      endIfAwayTooLong();
    };

    const onPageHide = () => markAuthAway();
    const onPageShow = () => endIfAwayTooLong();

    if (document.visibilityState === 'hidden') {
      markAuthAway();
    } else {
      endIfAwayTooLong();
    }

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [loggedInUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    window.localStorage.removeItem(STORAGE_KEYS.INTENDED_ROLE);
    window.sessionStorage.removeItem('finly_resume_auth');
    clearAuthAway();
    revokePortalAccess();
    await signOut(auth);
    setLoggedInUser(null);
    isInitialAuthRef.current = false;
    navigation.goToLanding();
  };

  const handleDeleteAccountPermanently = async () => {
    try {
      const { errors, profileDeleted } = await deleteAccountPermanently(loggedInUser);

      if (errors.includes('AUTH_DELETE_FAILED') || profileDeleted) {
        clearAuthAway();
        await signOut(auth);
      }

      if (errors.includes('AUTH_DELETE_FAILED')) {
        showToast(
          'Verileriniz silindi. Oturum güvenliği için çıkış yapıldı; giriş artık mümkün değil.',
          'success'
        );
      } else if (errors.length > 0) {
        showToast(
          'Hesabınız büyük ölçüde silindi. Bazı kayıtlar temizlenemedi; destek ile iletişime geçin.',
          'error'
        );
      } else {
        showToast('Hesabınız kalıcı olarak silinmiştir.', 'success');
      }

      setLoggedInUser(null);
      navigation.goToLanding();
      setActiveModal(null);
    } catch (e) {
      showToast(
        e.message || 'Güvenlik gereği hesabınızı silmek için lütfen çıkış yapıp tekrar giriş yapın.',
        'error'
      );
    }
  };

  return {
    firebaseUser,
    loggedInUser,
    setLoggedInUser,
    isLoading,
    handleLogout,
    handleDeleteAccountPermanently,
  };
};
